import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import GlobalStyles from '../components/GlobalStyles';
import { Sidebar } from '../components/Sidebar';
// import { HeroDashboard } from '../components/Hero'; // Converted to lazy
import { FeatureShowcase } from '../components/FeatureShowcase';

const HeroDashboard = React.lazy(() => import('../components/Hero').then(module => ({ default: module.HeroDashboard })));
import { WallOfFame } from '../components/WallOfFame';
import { QuickStart } from '../components/QuickStart';
import { RightPanel } from '../components/RightPanel';
import { Footer } from '../components/Footer';
import { Discover } from '../components/Discover';
import { Collections } from '../components/Collections';
import { GeneratorPage } from '../components/GeneratorPage';
import { Settings } from '../components/Settings';
import { PricingPage } from '../components/PricingPage';
import { StoryPage } from '../components/StoryPage';
import { StoryboardPage } from '../components/StoryboardPage';
const ShotDesignerPage = React.lazy(() => import('../components/shotDesigner/ShotDesignerPage'));
import { ProjectsPage } from '../components/Projects';
import { Profile } from '../components/Profile';
import { useAuth } from '../context/AuthContext';
import { LockIcon, ListIcon } from '@phosphor-icons/react';
import { MoodImageRequest } from '../types';
import PitchDeck from '../components/PitchDeck';
import { IdeaWorkspace } from '../components/idea';
import { IdeaProvider } from '../context/IdeaContext';
import { AtlasPage } from '../components/atlas';
import { DoodlePage } from '../components/doodle/DoodlePage';

import { CreativeSidebar } from '../components/CreativeSidebar';
import { Character2DBuilderPage } from '../components/Character2DBuilderPage';
import { OutfitBuilderPage } from '../components/OutfitBuilderPage';
// SpotifyPlayer SDK removed - requires Premium. Using embedded player in CreativeSidebar.

// Inner component that uses router hooks
const StudioLayoutInner: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get current view from path
    const currentView = location.pathname.slice(1) || 'studio';

    // Clean up any old hash routes on mount (from before migration)
    useEffect(() => {
        if (window.location.hash && !window.location.hash.includes('access_token') && !window.location.hash.includes('error')) {
            // Remove hash if it's an old navigation hash (not OAuth related)
            const hashParts = window.location.hash.substring(1).split('?');
            const hashValue = hashParts[0];
            const hashParams = hashParts[1] ? `?${hashParts[1]}` : '';
            // Merge hash params with existing search params
            const finalParams = hashParams || location.search;
            // If the hash matches a route name, redirect to that path instead
            if (hashValue && hashValue !== location.pathname.slice(1)) {
                console.log('🧹 Cleaning up old hash route, redirecting to:', hashValue, 'with params:', finalParams);
                window.history.replaceState(null, '', `/${hashValue}${finalParams}`);
            } else {
                // Just remove the hash but keep any params
                window.history.replaceState(null, '', window.location.pathname + finalParams);
            }
        }
    }, []); // Run only on mount

    // Sidebar state - persisted to localStorage
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('ezart_sidebar_open');
            return stored === null ? true : stored === 'true';
        }
        return true;
    });
    const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
    const closeMobileNavigation = useCallback(() => setIsMobileNavigationOpen(false), []);
    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 1024px)');
        const closeDrawer = () => { if (desktop.matches) setIsMobileNavigationOpen(false); };
        desktop.addEventListener('change', closeDrawer);
        return () => desktop.removeEventListener('change', closeDrawer);
    }, []);

    // Right Panel state - persisted, defaults to FALSE (collapsed)
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('ezart_rightpanel_open');
            return stored === null ? false : stored === 'true';
        }
        return false;
    });

    // Navigation Position - persisted, defaults to 'left'
    const [navPosition, setNavPosition] = useState<'left' | 'right'>(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('ezart_nav_position');
            return (stored === 'left' || stored === 'right') ? stored : 'left';
        }
        return 'left';
    });

    const { user } = useAuth();

    // Mood Image Generation State
    const [pendingMoodRequest, setPendingMoodRequest] = useState<MoodImageRequest | null>(null);

    // Persist states
    useEffect(() => {
        localStorage.setItem('ezart_sidebar_open', String(isSidebarOpen));
    }, [isSidebarOpen]);

    useEffect(() => {
        localStorage.setItem('ezart_rightpanel_open', String(isRightPanelOpen));
    }, [isRightPanelOpen]);

    useEffect(() => {
        localStorage.setItem('ezart_nav_position', navPosition);
    }, [navPosition]);

    const toggleNavPosition = () => {
        setNavPosition(prev => prev === 'left' ? 'right' : 'left');
    };

    // Define route permissions
    const routes: Record<string, boolean> = {
        studio: false,
        discover: false,
        projects: false,
        idea: false,
        story: false,
        atlas: false,
        character: false,
        world: false, // Coming Soon for all
        lookbook: false,
        doodle: false, // Sketch-to-anime drawing page
        video: false, // Coming Soon for all
        settings: false,
        outfit: false,
    };

    const handleNavigate = (view: string) => {
        const isProRoute = routes[view];

        if (!user && view !== 'studio' && view !== 'discover') {
            // In real app, redirect to login
            alert("Please sign in to access this feature.");
            return;
        }

        if (user?.plan === 'free' && isProRoute) {
            alert("This feature is available on the Pro Plan.");
            return;
        }

        // Use React Router navigation
        if (view === 'studio') {
            navigate('/');
        } else {
            navigate(`/${view}`);
        }
    };

    // Handle mood image navigation from CreativeSidebar
    const handleMoodNavigation = (moodRequest: MoodImageRequest) => {
        setPendingMoodRequest(moodRequest);
        // Navigate to lookbook page
        navigate('/lookbook');
    };

    // Page transition state
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [displayView, setDisplayView] = useState(currentView);

    // Keep creative pages mounted once visited (preserves state and ongoing work)
    const [mountedPages, setMountedPages] = useState<Set<string>>(new Set());

    // Track when creative views are accessed to keep them mounted
    const creativePagestoKeepMounted = ['character', 'outfit', 'lookbook', 'generator', 'idea', 'story', 'atlas', 'doodle'];
    useEffect(() => {
        if (creativePagestoKeepMounted.includes(displayView) && !mountedPages.has(displayView)) {
            setMountedPages(prev => new Set([...prev, displayView]));
        }
    }, [displayView]);

    // Handle page transitions
    useEffect(() => {
        if (currentView !== displayView) {
            setIsTransitioning(true);
            // Fade out current content
            const timer = setTimeout(() => {
                setDisplayView(currentView);
                setIsTransitioning(false);
            }, 150); // Half of the total transition duration
            return () => clearTimeout(timer);
        }
    }, [currentView, displayView]);

    // Unified loading spinner component used across all pages
    const LoadingSpinner = ({ message = "Loading..." }: { message?: string }) => (
        <div className="flex flex-col items-center justify-center gap-4 min-h-[50vh]">
            <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
            <p className="text-zinc-500 text-sm">{message}</p>
        </div>
    );

    const renderContent = () => {

        switch (displayView) {
            case 'studio':
                return (
                    <React.Suspense fallback={<LoadingSpinner message="Loading Studio..." />}>
                        <HeroDashboard />
                    </React.Suspense>
                );
            case 'discover':
                return <Discover />;
            case 'profile':
                // Parse query params from URL
                const searchParams = new URLSearchParams(location.search);
                const username = searchParams.get('username') || undefined;
                return <Profile username={username} />;
            case 'collections':
            case 'projects':
                return <ProjectsPage onNavigate={(view, projectId) => {
                    // Navigate to view with params
                    if (projectId) {
                        navigate(`/${view}?project=${projectId}`);
                    } else {
                        handleNavigate(view);
                    }
                }} />;
            case 'lookbook':
            case 'generator':
                return <GeneratorPage
                    pendingMoodRequest={pendingMoodRequest}
                    onMoodRequestComplete={() => setPendingMoodRequest(null)}
                />;
            case 'storyboard':
                return <StoryboardPage />;
            case 'shot-designer':
                return <React.Suspense fallback={<LoadingSpinner />}><ShotDesignerPage /></React.Suspense>;
            case 'story':
                return <StoryPage />;
            case 'settings':
                return <Settings />;
            case 'pricing':
                return <PricingPage onNavigate={handleNavigate} />;
            case 'pitch':
                return <PitchDeck />;
            case 'idea':
                return (
                    <IdeaProvider>
                        <IdeaWorkspace />
                    </IdeaProvider>
                );
            case 'atlas':
                return (
                    <IdeaProvider>
                        <AtlasPage />
                    </IdeaProvider>
                );
            case 'doodle':
                return <DoodlePage />;
            case 'world':
            case 'video':
                return (
                    <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 gap-6 min-h-[60vh]">
                        <div className="p-8 bg-zinc-900/50 rounded-2xl border border-zinc-800 flex flex-col items-center text-center max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mb-4 border border-zinc-800">
                                <LockIcon className="w-8 h-8 text-zinc-600" />
                            </div>
                            <h2 className="text-2xl text-white font-heading font-semibold mb-2">Coming Soon</h2>
                            <p className="text-zinc-400">
                                This feature is currently in development. We are focusing on perfecting the Story & Character workflow first.
                            </p>
                            <div className="mt-6 px-4 py-2 bg-indigo-500/10 text-indigo-400 text-xs font-bold rounded-full border border-indigo-500/20 uppercase tracking-widest">
                                Roadmap: Q4 2025
                            </div>
                        </div>
                    </div>
                );
            default:
                // Handle Not Logged In users trying to access protected routes
                if (!user && displayView !== 'studio' && displayView !== 'discover') {
                    return (
                        <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 gap-6 min-h-[60vh]">
                            <div className="p-8 bg-zinc-900/50 rounded-2xl border border-zinc-800 flex flex-col items-center text-center max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mb-4 border border-zinc-800">
                                    <LockIcon className="w-8 h-8 text-zinc-600" />
                                </div>
                                <h2 className="text-2xl text-white font-heading font-semibold mb-2">Sign In Required</h2>
                                <p className="text-zinc-400 mb-6">
                                    Please sign in to access the {displayView} studio.
                                </p>
                                <button
                                    onClick={() => navigate('/')}
                                    className="px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors"
                                >
                                    Back to Home
                                </button>
                            </div>
                        </div>
                    );
                }

                if (routes[displayView] && user?.plan !== 'pro') {
                    return (
                        <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 gap-4">
                            <div className="p-6 bg-zinc-900 rounded-full border border-zinc-800">
                                <LockIcon className="w-12 h-12 text-zinc-600" />
                            </div>
                            <h2 className="text-2xl text-white font-bold">Access Denied</h2>
                            <p>Upgrade to Pro to access {displayView}.</p>
                        </div>
                    )
                }
                return (
                    <LoadingSpinner message={`Loading ${displayView.charAt(0).toUpperCase() + displayView.slice(1)}...`} />
                );
        }
    };

    // Width Constants
    const NAV_WIDTH = 240;
    const NAV_COLLAPSED_WIDTH = 0; // Toggle handle is now hidden when collapsed (appears on hover)
    // Both Creative and Community panels share the same width now
    const PANEL_WIDTH = 640;
    const PANEL_COLLAPSED_WIDTH = 0; // Toggle handle is now hidden when collapsed (appears on hover)

    // Calculate paddings based on state
    const navWidth = isSidebarOpen ? NAV_WIDTH : NAV_COLLAPSED_WIDTH;
    const panelWidth = isRightPanelOpen ? PANEL_WIDTH : PANEL_COLLAPSED_WIDTH;

    return (
        <div className="min-h-screen selection:bg-purple-500/30 selection:text-white overflow-x-hidden">
            <GlobalStyles />
            {/* Spotify Web Playback SDK removed - requires Premium. Using embedded player instead. */}
            <div className="atelier-ambient" />

            {/* Navigation Sidebar */}
            <Sidebar
                currentView={currentView}
                onNavigate={handleNavigate}
                isOpen={isSidebarOpen}
                onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
                side={navPosition}
                onSwapSides={toggleNavPosition}
                mobileOpen={isMobileNavigationOpen}
                onMobileClose={closeMobileNavigation}
            />

            {/* Right Panel / Creative Sidebar */}
            {/* We keep CreativeSidebar always mounted to persist the Music Player state (YouTube iframe, etc.) */}
            <CreativeSidebar
                isOpen={isRightPanelOpen}
                onToggle={() => setIsRightPanelOpen(!isRightPanelOpen)}
                side={navPosition === 'left' ? 'right' : 'left'}
                onSwapSides={toggleNavPosition}
                onNavigateToLookbook={handleMoodNavigation}
                onClearProject={() => navigate('/')}
                isActive={['idea', 'story', 'atlas', 'character', 'world', 'lookbook', 'video', 'storyboard', 'shot-designer', 'doodle'].includes(currentView)}
            />

            {/* Community Right Panel - Only render when CreativeSidebar is NOT active */}
            {!['idea', 'story', 'atlas', 'character', 'world', 'lookbook', 'video', 'storyboard', 'shot-designer', 'doodle'].includes(currentView) && (
                <RightPanel
                    isOpen={isRightPanelOpen}
                    onToggle={() => setIsRightPanelOpen(!isRightPanelOpen)}
                    side={navPosition === 'left' ? 'right' : 'left'}
                    onSwapSides={toggleNavPosition}
                    projectId={new URLSearchParams(location.search).get('project') || undefined}
                />
            )}

            {/* Main Layout Wrapper */}
            <main
                data-nav-position={navPosition}
                className={`studio-shell relative z-10 w-full h-screen overflow-hidden flex flex-col transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${['studio', 'discover', 'projects', 'collections', 'profile', 'settings', 'pricing', 'pitch', 'outfit', 'atlas'].includes(currentView) ? 'bg-onyx' : 'bg-carbon'
                    }`}
                style={{
                    paddingLeft: 'var(--sidebar-left)',
                    paddingRight: 'var(--sidebar-right)',
                    ['--nav-width' as string]: `${navWidth}px`,
                    ['--panel-width' as string]: `${panelWidth}px`,
                }}
            >
                <div className="lg:hidden flex items-center gap-3 px-4 h-12 shrink-0 border-b border-white/5 bg-onyx">
                    <button id="studio-navigation-toggle" aria-label="Open navigation" aria-controls="studio-navigation"
                        aria-expanded={isMobileNavigationOpen} className="flex items-center gap-2 text-sm text-zinc-200"
                        onClick={() => setIsMobileNavigationOpen(true)}>
                        <ListIcon className="w-5 h-5" /> Menu
                    </button>
                    <span className="font-heading text-white">EZ Art.</span>
                </div>
                <div
                    className={`w-full flex-1 min-h-0 flex flex-col transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
                >
                    {/* Character Builder - kept mounted once visited */}
                    {mountedPages.has('character') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'character' ? '' : 'hidden'}`}>
                            <React.Suspense fallback={<LoadingSpinner message="Loading Character Builder..." />}>
                                <Character2DBuilderPage onBack={() => handleNavigate('projects')} />
                            </React.Suspense>
                        </div>
                    )}

                    {/* Outfit Builder - kept mounted once visited */}
                    {mountedPages.has('outfit') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'outfit' ? '' : 'hidden'}`}>
                            <React.Suspense fallback={<LoadingSpinner message="Loading Wardrobe..." />}>
                                <OutfitBuilderPage
                                    characterId={new URLSearchParams(location.search).get('characterId') || undefined}
                                    projectId={new URLSearchParams(location.search).get('projectId') || undefined}
                                    onBack={() => handleNavigate('projects')}
                                />
                            </React.Suspense>
                        </div>
                    )}

                    {/* Lookbook/Generator - kept mounted once visited */}
                    {(mountedPages.has('lookbook') || mountedPages.has('generator')) && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'lookbook' || displayView === 'generator' ? '' : 'hidden'}`}>
                            <GeneratorPage
                                pendingMoodRequest={pendingMoodRequest}
                                onMoodRequestComplete={() => setPendingMoodRequest(null)}
                            />
                        </div>
                    )}

                    {/* Idea Workspace - kept mounted once visited */}
                    {mountedPages.has('idea') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'idea' ? '' : 'hidden'}`}>
                            <IdeaProvider>
                                <IdeaWorkspace />
                            </IdeaProvider>
                        </div>
                    )}

                    {/* Story Page - kept mounted once visited */}
                    {mountedPages.has('story') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'story' ? '' : 'hidden'}`}>
                            <StoryPage />
                        </div>
                    )}

                    {/* Atlas Page - kept mounted once visited */}
                    {mountedPages.has('atlas') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'atlas' ? '' : 'hidden'}`}>
                            <IdeaProvider>
                                <AtlasPage />
                            </IdeaProvider>
                        </div>
                    )}

                    {/* Doodle Page - kept mounted once visited */}
                    {mountedPages.has('doodle') && (
                        <div className={`w-full h-full flex flex-col ${displayView === 'doodle' ? '' : 'hidden'}`}>
                            <DoodlePage />
                        </div>
                    )}

                    {/* Other views - normal conditional rendering (studio, discover, projects, settings, etc.) */}
                    {!creativePagestoKeepMounted.includes(displayView) && (
                        displayView === 'studio' ? (
                            renderContent()
                        ) : (
                            <div className={displayView === 'shot-designer' ? "flex-1 min-h-0 overflow-hidden" : "flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent"}>
                                {renderContent()}
                                {displayView !== 'pricing' && displayView !== 'shot-designer' && <Footer />}
                            </div>
                        )
                    )}
                </div>
            </main>
            {/* Persistent Miniplayer Portal Root */}
            <div id="miniplayer-portal-root" className="fixed z-[100]" />
        </div>
    );
};

// Wrapper component with BrowserRouter
export const StudioLayout: React.FC = () => {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<StudioLayoutInner />} />
                <Route path="/:view/*" element={<StudioLayoutInner />} />
            </Routes>
        </BrowserRouter>
    );
};
